import {
  ConfigField,
  ConfigTab,
  ConfigValue,
  EnvironmentAdminConfig,
  EnvironmentContext,
  EnvironmentFormState,
} from './adminConfig.types';
import { Lookup, applicationFields, evaluateCondition, fillMessage, flatFields, makeLookup, sortedTabs } from './configRules';

/**
 * Client-side validation driven by the admin config — the same rules and messages the backend
 * applies (`Entities/env_config_schema.py`), plus the rules listed in a field's `clientOnly`
 * (name uniqueness/pattern, required store-front URL, ...), which only the panel enforces.
 */

// Same expression as the backend's URL_PATTERN.
const URL_PATTERN =
  /^https?:\/\/(?:(?:[A-Z0-9](?:[A-Z0-9-]{0,61}[A-Z0-9])?\.)+[A-Z]{2,6}\.?|localhost|\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})(?::\d+)?(?:\/?|[/?]\S+)$/i;

/** Caps every value gets even when its config field sets none — the backend's
 * `DEFAULT_TEXT_MAX_LENGTH` / `LIST_MAX_ITEMS` / `LIST_ITEM_MAX_LENGTH` / `KEY_MAX_LENGTH`
 * (`Entities/env_config_schema.py`). JS cannot hold int64 exactly, so integers are capped at
 * `Number.MAX_SAFE_INTEGER`, inside the backend's int64 bound. */
export const DEFAULT_TEXT_MAX_LENGTH = 10000;
export const LIST_MAX_ITEMS = 100;
export const LIST_ITEM_MAX_LENGTH = 1000;
export const KEY_MAX_LENGTH = 100;

const DEFAULT_MESSAGES: Record<string, string> = {
  required: '{label} is required',
  length: '{label} must be at most {max} characters',
  pattern: '{label} has an invalid format',
  format: 'Invalid URL format. Must include http:// or https://',
  options: '{label} must be one of: {options}',
  type: '{label} has an invalid value',
  range: '{label} must be between {min} and {max}',
  step: '{label} must be a multiple of {step}',
  sum: '{label} must add up to {sum}',
  lte: '{label}: the first value cannot be more than the second',
  item: '{label} contains an invalid value: {value}',
  unique: '{label} must be unique',
  count: '{label} can have at most {max} entries',
};

export const message = (field: ConfigField, rule: string, values: Record<string, ConfigValue> = {}): string =>
  fillMessage(field.messages?.[rule] ?? DEFAULT_MESSAGES[rule] ?? DEFAULT_MESSAGES.type, {
    label: field.label ?? field.key,
    ...values,
  });

const isBlank = (value: ConfigValue): boolean =>
  value === undefined || value === null || (typeof value === 'string' && value.trim() === '');

const toNumber = (value: ConfigValue): number | null => {
  const number =
    typeof value === 'number' ? value : typeof value === 'string' && value.trim() !== '' ? Number(value) : NaN;
  if (!Number.isFinite(number)) return null;
  // A whole number beyond this cannot be stored exactly (the backend's int64 bound is wider).
  return Number.isInteger(number) && Math.abs(number) > Number.MAX_SAFE_INTEGER ? null : number;
};

const checkRange = (field: ConfigField, number: number): string | null => {
  const { min, max, step, integer } = field.validation ?? {};
  if (integer && !Number.isInteger(number)) return message(field, 'type');
  if ((min !== undefined && number < min) || (max !== undefined && number > max)) {
    return message(field, 'range', { min: min ?? '-', max: max ?? '-' });
  }
  if (step) {
    const ratio = number / step;
    if (Math.abs(ratio - Math.round(ratio)) > 1e-9) return message(field, 'step', { step });
  }
  return null;
};

/** The first problem with one value, or null. `lookup` resolves conditions; `ctx` adds name uniqueness. */
export const validateValue = (
  field: ConfigField,
  value: ConfigValue,
  lookup: Lookup,
  ctx?: Pick<EnvironmentContext, 'existingEnvironmentNames' | 'currentEnvironmentName'>,
): string | null => {
  const rules = field.validation ?? {};
  switch (field.type) {
    case 'text':
    case 'url':
    case 'secret': {
      if (typeof value === 'object' && value !== null) return message(field, 'type');
      const text = value === undefined || value === null ? '' : String(value);
      const trimmed = rules.trim || field.passThrough ? text.trim() : text;
      const required =
        rules.required || (rules.requiredWhen !== undefined && evaluateCondition(rules.requiredWhen, lookup));
      if (trimmed.trim() === '') return required ? message(field, 'required') : null;
      const maxLength = rules.maxLength ?? DEFAULT_TEXT_MAX_LENGTH;
      if (trimmed.length > maxLength) return message(field, 'length', { max: maxLength });
      if (rules.pattern && !new RegExp(rules.pattern).test(trimmed)) return message(field, 'pattern');
      const formatApplies = rules.formatWhen === undefined || evaluateCondition(rules.formatWhen, lookup);
      if (rules.format === 'url' && formatApplies && !URL_PATTERN.test(trimmed)) return message(field, 'format');
      if (rules.unique && ctx) {
        const clash = ctx.existingEnvironmentNames.some(
          (name) =>
            name.toLowerCase() === trimmed.toLowerCase() &&
            name.toLowerCase() !== (ctx.currentEnvironmentName ?? '').toLowerCase(),
        );
        if (clash) return message(field, 'unique');
      }
      return null;
    }
    case 'select': {
      const empty = field.emptyValues ?? [];
      if (value === undefined || value === null || empty.includes(value)) {
        return rules.required ? message(field, 'required') : field.emptyValues ? null : message(field, 'type');
      }
      const options = (field.options ?? []).map((option) => option.value);
      if (options.length && !options.includes(value as never)) {
        return message(field, 'options', { options: options.join(', ') });
      }
      return null;
    }
    case 'number': {
      if (isBlank(value)) return field.nullable ? null : message(field, 'type');
      const number = toNumber(value);
      if (number === null) return message(field, 'type');
      return checkRange(field, number);
    }
    case 'stringList': {
      if (!Array.isArray(value)) return message(field, 'type');
      if (value.filter((item) => typeof item !== 'string' || item.trim()).length > LIST_MAX_ITEMS) {
        return message(field, 'count', { max: LIST_MAX_ITEMS });
      }
      const pattern = rules.itemPattern ? new RegExp(rules.itemPattern) : null;
      const bad = value.find(
        (item) =>
          typeof item !== 'string' || item.trim().length > LIST_ITEM_MAX_LENGTH || (pattern && !pattern.test(item.trim())),
      );
      return bad === undefined ? null : message(field, 'item', { value: typeof bad === 'string' ? bad.trim().slice(0, 50) : bad });
    }
    case 'keyValueList': {
      if (typeof value !== 'object' || value === null || Array.isArray(value)) return message(field, 'type');
      const entries = Object.entries(value as Record<string, ConfigValue>);
      if (entries.length > LIST_MAX_ITEMS) return message(field, 'count', { max: LIST_MAX_ITEMS });
      for (const [key, raw] of entries) {
        const number = toNumber(raw);
        const name = key.trim();
        // Keys become Mongo field names on the backend: no dots, no leading $.
        const badKey = !name || name.length > KEY_MAX_LENGTH || name.includes('.') || name.startsWith('$');
        if (badKey || number === null) return message(field, 'item', { value: key.slice(0, 50) });
        if ((rules.itemMin !== undefined && number < rules.itemMin) || (rules.itemMax !== undefined && number > rules.itemMax)) {
          return message(field, 'item', { value: key });
        }
      }
      return null;
    }
    default:
      return null;
  }
};

/** Validate a group's children and its sumEquals / lte rule. Errors keyed `<path>` / `<path>.<child>`. */
const validateGroup = (field: ConfigField, value: ConfigValue, path: string, lookup: Lookup, errors: Record<string, string>) => {
  const values = (value ?? {}) as Record<string, ConfigValue>;
  for (const child of field.children ?? []) {
    const error = validateValue(child, values[child.key], lookup);
    if (error) errors[`${path}.${child.key}`] = error;
  }
  const rules = field.validation ?? {};
  if (rules.sumEquals !== undefined) {
    const parts = (rules.fields ?? (field.children ?? []).map((c) => c.key)).map((key) => toNumber(values[key]));
    if (parts.every((part) => part !== null) && Math.abs((parts as number[]).reduce((a, b) => a + b, 0) - rules.sumEquals) > 1e-9) {
      errors[path] = message(field, 'sum', { sum: rules.sumEquals });
    }
  }
  if (rules.lte) {
    const [low, high] = rules.lte.map((key) => toNumber(values[key]));
    if (low !== null && high !== null && low > high) errors[path] = message(field, 'lte');
  }
};

const validateObjectList = (field: ConfigField, value: ConfigValue, path: string, lookup: Lookup, errors: Record<string, string>) => {
  if (!Array.isArray(value)) {
    errors[path] = message(field, 'type');
    return;
  }
  if (value.length > LIST_MAX_ITEMS) {
    errors[path] = message(field, 'count', { max: LIST_MAX_ITEMS });
    return;
  }
  value.forEach((item, index) => {
    for (const itemField of field.itemFields ?? []) {
      const error = validateValue(itemField, (item as Record<string, ConfigValue>)?.[itemField.key], lookup);
      if (error) errors[`${path}.${index}.${itemField.key}`] = error;
    }
  });
};

/** Every error in the form, keyed like the backend (`dbKey` or `applications.<tab>.<key>…`). */
export const validateAll = (
  state: EnvironmentFormState,
  config: EnvironmentAdminConfig,
  ctx: EnvironmentContext,
): Record<string, string> => {
  const errors: Record<string, string> = {};
  for (const { field, tab } of flatFields(config)) {
    if (field.readOnly && !field.passThrough) continue;
    const error = validateValue(field, state.flat[field.dbKey as string], makeLookup(state, tab, ctx.responseValues), ctx);
    if (error) errors[field.dbKey as string] = error;
  }
  for (const tab of sortedTabs(config).filter((t) => !t.apiSection)) {
    const lookup = makeLookup(state, tab, ctx.responseValues);
    for (const field of applicationFields(tab)) {
      const path = `applications.${tab.id}.${field.key}`;
      const value = state.applications[tab.id]?.[field.key];
      if (field.type === 'group') validateGroup(field, value, path, lookup, errors);
      else if (field.type === 'objectList') validateObjectList(field, value, path, lookup, errors);
      else {
        const error = validateValue(field, value, lookup);
        if (error) errors[path] = error;
      }
    }
  }
  return errors;
};

/** The tab a validation error belongs to, so the panel can point the user at it. */
export const tabOfError = (config: EnvironmentAdminConfig, errorKey: string): ConfigTab | undefined => {
  if (errorKey.startsWith('applications.')) return config.tabs.find((tab) => tab.id === errorKey.split('.')[1]);
  return flatFields(config).find(({ field }) => field.dbKey === errorKey)?.tab;
};
